import { useEffect, useState } from 'react';
import axios from 'axios';
import { Badge } from './ui/badge';
import { styleForEvent, fmtRelative, summarize, roleColor } from './activityFeedShared';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const opts = { withCredentials: true };

const COUNT_LABELS = {
  'page.viewed': ['page view', 'page views'],
  'product.viewed': ['product view', 'product views'],
  'order.tracked_viewed': ['tracking', 'tracking'],
  'order.placed': ['order', 'orders'],
  'cart.add': ['cart add', 'cart adds'],
  'search.performed': ['search', 'searches'],
  'auth.login': ['login', 'logins'],
};

function countLine(counts) {
  const parts = Object.entries(COUNT_LABELS)
    .filter(([k]) => counts[k])
    .map(([k, [one, many]]) => `${counts[k]} ${counts[k] === 1 ? one : many}`);
  const other = Object.entries(counts).filter(([k]) => !COUNT_LABELS[k]).reduce((a, [, v]) => a + v, 0);
  if (other) parts.push(`${other} other`);
  return parts.join(' · ');
}

function PersonTimeline({ customerId, qs, refreshKey, total }) {
  const [events, setEvents] = useState(null);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    axios.get(`${API_URL}/api/admin/activity-feed?${qs}&customer_id=${customerId}&limit=500`, opts)
      .then(({ data }) => setEvents(data.events || []))
      .catch(() => setEvents([]));
  }, [customerId, qs, refreshKey]);
  if (!events) return <div className="text-[#888] text-[11px] py-2 pl-3">Loading…</div>;
  const shown = showAll ? events : events.slice(0, 30);
  return (
    <div className="mt-2 ml-1 pl-3 border-l border-dashed border-[#33FF57]/40 space-y-1" data-testid={`activity-person-timeline-${customerId}`}>
      {shown.map((ev) => {
        const s = styleForEvent(ev.event_type);
        return (
          <div key={ev.id} className="flex items-center gap-2 text-[11px]">
            <span className="px-1 rounded-sm text-[9px] font-bold whitespace-nowrap"
              style={{ background: `${s.color}22`, color: s.color, border: `1px solid ${s.color}55` }}>{s.label}</span>
            <span className="text-[#A3C7A8] truncate flex-1 min-w-0">{summarize(ev.payload, ev.event_type, true)}</span>
            <span className="text-[#888] text-[10px] whitespace-nowrap">{fmtRelative(ev.ts)}</span>
          </div>
        );
      })}
      {events.length > shown.length && (
        <button type="button" onClick={() => setShowAll(true)} className="text-[11px] text-[#33FF57] hover:underline"
          data-testid={`activity-person-show-all-${customerId}`}>
          + {events.length - shown.length} more · Show all
        </button>
      )}
      {showAll && total > events.length && (
        <div className="text-[10px] text-[#888]">// showing latest {events.length} of {total} — narrow the time range to see older ones</div>
      )}
    </div>
  );
}

export function ActivityPeopleList({ qs, refreshKey, onOpenProfile, onLoaded }) {
  const [people, setPeople] = useState(null);
  const [openId, setOpenId] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/api/admin/activity-people?${qs}`, opts)
      .then(({ data }) => { setPeople(data.people || []); onLoaded?.(data); })
      .catch(() => setPeople([]));
  }, [qs, refreshKey, onLoaded]);

  if (!people) return <div className="text-center text-[#888] text-sm py-8">Loading…</div>;
  if (!people.length) return <div className="text-center text-[#888] text-sm py-8">// no activity in window</div>;
  return (
    <div className="max-h-[600px] overflow-y-auto pr-1 space-y-1" data-testid="activity-people-list">
      {people.map((p) => {
        const open = openId === p.customer_id;
        return (
          <div key={p.customer_id} data-testid={`activity-person-${p.customer_id}`}
            className={`px-2 py-2 rounded border text-xs font-mono transition-colors ${open ? 'border-[#33FF57]/70 bg-black/40' : 'border-[var(--terminal-border)] hover:bg-black/40'}`}>
            <button type="button" onClick={() => setOpenId(open ? null : p.customer_id)} className="w-full text-left"
              data-testid={`activity-person-toggle-${p.customer_id}`}>
              <div className="flex items-center gap-2">
                <span className="truncate min-w-0" style={{ color: roleColor(p.role) }}>{p.name || p.email || p.customer_id.slice(0, 8)}</span>
                {p.region && <Badge variant="outline" className="text-[10px] px-1 py-0">{p.region}</Badge>}
                <span className="ml-auto text-[#888] text-[10px] whitespace-nowrap">{fmtRelative(p.last_ts)}</span>
              </div>
              <div className="text-[#A3C7A8] text-[11px] mt-0.5" data-testid={`activity-person-counts-${p.customer_id}`}>{countLine(p.counts)}</div>
            </button>
            {open && (
              <>
                <PersonTimeline customerId={p.customer_id} qs={qs} refreshKey={refreshKey} total={p.total} />
                <button type="button" onClick={() => onOpenProfile(p.customer_id)}
                  className="mt-2 text-[11px] text-[#7FD8FF] hover:underline" data-testid={`activity-person-profile-${p.customer_id}`}>
                  Open profile →
                </button>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
